/**
 * Transient Error Filter
 * 
 * Implements 3-consecutive-failures threshold before showing error toasts to users.
 * Transient errors like 503, BOOT_ERROR, and network issues are suppressed until
 * they occur 3 times in a row without any successful call in between.
 */

export const RETRYABLE_PATTERNS = /503|500|BOOT_ERROR|Network connection lost|Failed to fetch|FunctionsFetchError|timeout|Worker threw exception|Function failed to start/i;

interface ErrorTracker {
  functionName: string;
  consecutiveFailures: number;
  lastError: Date;
  lastSuccess: Date | null;
  lastErrorMessage: string;
}

interface TransientErrorFilterConfig {
  threshold: number; // Number of consecutive failures before surfacing error
  decayTimeMs: number; // Time after which failure count resets
}

const DEFAULT_CONFIG: TransientErrorFilterConfig = {
  threshold: 3,
  decayTimeMs: 60000, // 1 minute
};

export class TransientErrorFilter {
  private trackers = new Map<string, ErrorTracker>();
  private config: TransientErrorFilterConfig;

  constructor(config: Partial<TransientErrorFilterConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Check if an error is a transient/retryable error
   */
  isTransientError(error: Error | string): boolean {
    const message = typeof error === 'string' ? error : error.message || '';
    return RETRYABLE_PATTERNS.test(message);
  }

  /**
   * Record a successful call (resets consecutive failure counter)
   */
  recordSuccess(functionName: string): void {
    const tracker = this.trackers.get(functionName);
    if (tracker) {
      tracker.consecutiveFailures = 0;
      tracker.lastSuccess = new Date();
    }
  }

  /**
   * Record a failure and determine if it should be shown to the user
   * Returns true if the error should be surfaced (threshold reached)
   * Returns false if the error should be suppressed (under threshold)
   */
  recordFailure(functionName: string, error: Error | string): boolean {
    const errorMessage = typeof error === 'string' ? error : error.message || '';
    const now = new Date();
    
    let tracker = this.trackers.get(functionName);
    
    if (!tracker) {
      tracker = {
        functionName,
        consecutiveFailures: 0,
        lastError: now,
        lastSuccess: null,
        lastErrorMessage: errorMessage,
      };
      this.trackers.set(functionName, tracker);
    }
    
    // Check if enough time has passed to reset the counter
    const timeSinceLastError = now.getTime() - tracker.lastError.getTime();
    if (timeSinceLastError > this.config.decayTimeMs) {
      tracker.consecutiveFailures = 0;
    }
    
    // Increment failure count
    tracker.consecutiveFailures++;
    tracker.lastError = now;
    tracker.lastErrorMessage = errorMessage;
    
    // Return true if threshold reached (error should be shown)
    return tracker.consecutiveFailures >= this.config.threshold;
  }

  /**
   * Get the current failure count for a function
   */
  getFailureCount(functionName: string): number {
    return this.trackers.get(functionName)?.consecutiveFailures ?? 0;
  }

  /**
   * Get all tracked functions with their current status
   */
  getAllTrackers(): Map<string, ErrorTracker> {
    return new Map(this.trackers);
  }

  /**
   * Check if a function has reached the failure threshold
   */
  hasReachedThreshold(functionName: string): boolean {
    const count = this.getFailureCount(functionName);
    return count >= this.config.threshold;
  }

  /**
   * Reset tracker for a specific function
   */
  reset(functionName: string): void {
    this.trackers.delete(functionName);
  }

  /**
   * Reset all trackers
   */
  resetAll(): void {
    this.trackers.clear();
  }

  /**
   * Get remaining failures until threshold for a function
   */
  getRemainingUntilThreshold(functionName: string): number {
    const count = this.getFailureCount(functionName);
    return Math.max(0, this.config.threshold - count);
  }
}

// Singleton instance with default config
export const transientErrorFilter = new TransientErrorFilter();

// Factory function for custom configs
export function createTransientErrorFilter(config?: Partial<TransientErrorFilterConfig>): TransientErrorFilter {
  return new TransientErrorFilter(config);
}
